import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Rocket } from "lucide-react";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | success | timeout | error

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let attempts = 0;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid" || data.status === "completed") { setState("success"); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
            } catch (e) { /* retry */ }
            if (attempts >= 8) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-ares-void px-6">
            <div className="w-full max-w-md text-center rounded-2xl border border-ares-border bg-ares-dark/90 p-10 fade-up" data-testid="payment-success-card">
                {state === "checking" && (
                    <>
                        <Loader2 className="w-12 h-12 text-ares-orange animate-spin mx-auto mb-5" />
                        <h1 className="font-heading text-2xl font-semibold">Confirming your trial…</h1>
                        <p className="text-ares-muted mt-2 text-sm">Securing your ARES Pro access.</p>
                    </>
                )}
                {state === "success" && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-ares-emerald/15 border border-ares-emerald/40 flex items-center justify-center mx-auto mb-5">
                            <CheckCircle2 className="w-8 h-8 text-ares-emerald" />
                        </div>
                        <h1 className="font-heading text-2xl font-semibold">Welcome to ARES Pro! 🚀</h1>
                        <p className="text-ares-muted mt-2 text-sm">Your 14-day free trial is active. Full mission access unlocked.</p>
                        <Link to="/app"><Button className="mt-6 h-11 bg-ares-orange hover:bg-[#e55a2b] text-white px-8" data-testid="success-continue-btn"><Rocket className="w-4 h-4 mr-2" /> Enter Console</Button></Link>
                    </>
                )}
                {(state === "timeout" || state === "error") && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-ares-amber/15 border border-ares-amber/40 flex items-center justify-center mx-auto mb-5">
                            <XCircle className="w-8 h-8 text-ares-amber" />
                        </div>
                        <h1 className="font-heading text-2xl font-semibold">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
                        <p className="text-ares-muted mt-2 text-sm">{state === "timeout" ? "Your payment is taking a moment. It may complete shortly." : "We couldn't confirm the payment."}</p>
                        <Link to="/app"><Button variant="outline" className="mt-6 h-11 border-ares-border bg-transparent hover:bg-ares-panel text-ares-bright px-8" data-testid="back-to-app-btn">Back to Console</Button></Link>
                    </>
                )}
            </div>
        </div>
    );
}
